import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AuthApi from "../api/auth-api";
import { ILogin, TAuth } from "../types";
import { RootState } from "../app/store";

const initialState: TAuth = {
  authStatus: false,
  editor: false,
  publisher: false,
  user: false,
  username: "",
  loading: false,
  error: null,
  status: null,
  statusMessage: "",
  privilege: "",
  affiliateId: null,
  loginTimestamp: 0,
  password: "",
  sessionid: "",
  vmLoginTimestamp: 0,
};

export const login = createAsyncThunk(
  "auth/login",
  async (request: ILogin, thunkApi) => {
    try {
      const responseData = await AuthApi.login(request);

      return thunkApi.fulfillWithValue({ ...responseData, ...request });
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const vmLogin = createAsyncThunk(
  "auth/vmLogin",
  async (request: ILogin, thunkApi) => {
    try {
      const responseData = await AuthApi.vmLogin(request);
      return thunkApi.fulfillWithValue({ ...responseData, ...request });
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const userLogin = createAsyncThunk(
  "auth/userLogin",
  async (request: ILogin, thunkApi) => {
    try {
      const responseData = await AuthApi.userLogin(request);
      return thunkApi.fulfillWithValue({ ...responseData, ...request });
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

const loginSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logOut: (state) => {
      state.authStatus = false;
      state.editor = false;
      state.publisher = false;
      state.user = false;
      state.username = "";
      state.loginTimestamp = 0;
      state.affiliateId = null;
      state.statusMessage = "";
      state.privilege = "";
      state.status = null;
      state.loading = false;
      state.password = "";
      state.sessionid = "";
      state.vmLoginTimestamp = 0;
    },
    updateLoginTimestamp: (state) => {
      state.loginTimestamp = new Date().getTime();
    },
    updateVMLoginTimestamp: (state) => {
      state.vmLoginTimestamp = new Date().getTime();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.privilege = action.payload?.privilege ?? "";
        if (action.payload?.status === 0) {
          state.authStatus = true;
          state.editor = state.privilege === "THEMEEDITOR";
          state.publisher = state.privilege === "THEMEPUBLISHER";
          // state.password = action.payload?.password ?? "";
          state.username = action.payload?.username ?? "";
          state.loginTimestamp = new Date().getTime();
        }
      })
      .addCase(login.rejected, (state, action) => {
        return initialState;
      });

    builder
      .addCase(vmLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(vmLogin.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.status === 0) {
          state.authStatus = true;
          state.sessionid = action.payload?.data?.sessionid ?? "";
          // state.password = action.payload?.password ?? "";
          state.username = action.payload?.data?.username ?? "";
          state.loginTimestamp = new Date().getTime();
          // state.vmLoginTimestamp = new Date().getTime();
        }
      })
      .addCase(vmLogin.rejected, (state, action) => {
        return initialState;
      });

    builder
      .addCase(userLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        state.loading = false;
        if (
          action.payload?.status === 0 &&
          action.payload.data?.userdefault.themeeditorallowed === "Y"
        ) {
          state.authStatus = true;
          state.affiliateId =
            action.payload.data?.distributorprofile.affiliateid;
          state.sessionid = action.payload?.data?.sessionid ?? "";
          // state.password = action.payload?.password ?? "";
          state.username = action.payload.data?.username ?? "";
          state.user = true;
          state.loginTimestamp = new Date().getTime();
          // state.vmLoginTimestamp = new Date().getTime();
        } else {
          state.statusMessage =
            "Theme Editor Access is not allowed for this user";
        }
      })
      .addCase(userLogin.rejected, (state, action) => {
        return initialState;
      });
  },
});

// Export actions
export const { logOut, updateLoginTimestamp } = loginSlice.actions;

// Selectors remain the same
export const auth = (state: RootState) => state.auth.authStatus;
export const editor = (state: RootState) => state.auth.editor;
export const user = (state: RootState) => state.auth.user;
export const publisher = (state: RootState) => state.auth.publisher;
export const affiliateId = (state: RootState) => state.auth.affiliateId;
export const authStatusMessage = (state: RootState) => state.auth.statusMessage;
export const authSessionid = (state: RootState) => state.auth.sessionid;
export const authUsername = (state: RootState) => state.auth.username;
export const authPassword = (state: RootState) => state.auth.password;
export const authPrivilege = (state: RootState) => state.auth.privilege;
export const authVmLoginTimestamp = (state: RootState) => state.auth.vmLoginTimestamp;
export const authLoginTimestamp = (state: RootState) => state.auth.loginTimestamp;

export default loginSlice.reducer;
